import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { CalendarDays } from "lucide-react";
import { fr } from "date-fns/locale";

// Map day index (0=Sunday, 1=Monday...) → english name used in service.allowed_weekdays
const IDX_TO_NAME = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

const DAY_FR = {
  sunday: "dim", monday: "lun", tuesday: "mar", wednesday: "mer",
  thursday: "jeu", friday: "ven", saturday: "sam",
};

const dayKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/**
 * WeekdayDatePicker — Date picker that disables weekdays not in `allowedWeekdays`
 * and full days according to `availability` ({capacity, days: {"YYYY-MM-DD": count}}).
 */
export default function WeekdayDatePicker({ value, onChange, allowedWeekdays = [], availability = null, testid = "weekday-date-picker", placeholder = "Sélectionner une date" }) {
  const [open, setOpen] = useState(false);

  const selected = value ? new Date(value) : undefined;
  const capacity = availability?.capacity || 0;
  const days = availability?.days || {};
  const remainingFor = (date) => (capacity ? Math.max(capacity - (days[dayKey(date)] || 0), 0) : null);

  const isDisabled = (date) => {
    if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
    if (capacity && remainingFor(date) === 0) return true;
    if (!allowedWeekdays || allowedWeekdays.length === 0) return false;
    return !allowedWeekdays.includes(IDX_TO_NAME[date.getDay()]);
  };

  const onSelect = (date) => {
    if (!date) return;
    onChange?.(dayKey(date));
    setOpen(false);
  };

  const display = selected
    ? selected.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : placeholder;

  const allowedLabels = allowedWeekdays.map((d) => DAY_FR[d] || d).join(", ");

  const DayContent = ({ date }) => {
    const rem = remainingFor(date);
    const past = date < new Date(new Date().setHours(0, 0, 0, 0));
    const showDot = capacity && !past && rem !== null && rem < capacity;
    return (
      <div className="relative flex flex-col items-center justify-center leading-none">
        <span>{date.getDate()}</span>
        {showDot && (
          <span
            className={`absolute -bottom-1.5 text-[8px] font-bold ${
              rem === 0 ? "text-red-500" : rem === 1 ? "text-orange-500" : "text-green-600"
            }`}
          >
            {rem === 0 ? "complet" : `${rem} pl.`}
          </span>
        )}
      </div>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="lc-input text-left flex items-center justify-between gap-2 cursor-pointer"
        >
          <span className={selected ? "text-[var(--lc-black)]" : "text-[var(--lc-gray-light)]"}>
            {display}
          </span>
          <CalendarDays className="w-4 h-4 text-[var(--lc-gray)] shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        {allowedWeekdays.length > 0 && (
          <div className="px-3 pt-3 pb-1 text-[11px] text-[var(--lc-gray)] border-b border-[var(--lc-border)]">
            Jours disponibles : <span className="font-semibold text-[var(--lc-black)] uppercase">{allowedLabels}</span>
          </div>
        )}
        <Calendar
          mode="single"
          selected={selected}
          onSelect={onSelect}
          disabled={isDisabled}
          locale={fr}
          weekStartsOn={1}
          components={{ DayContent }}
          initialFocus
        />
        {capacity > 0 && (
          <div className="px-3 pb-3 pt-1 text-[10px] text-[var(--lc-gray)] border-t border-[var(--lc-border)]">
            <span className="font-semibold">{capacity} livraisons max/jour.</span> Les jours « complet » ne sont plus sélectionnables.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
